import { ErrorOutline, Search, ShieldOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { FormEvent, RefObject, useEffect, useRef, useState } from 'react';

import CustomerIntakePublicShell from '../components/CustomerIntake/CustomerIntakePublicShell';
import { useTranslation } from '../i18n';
import {
  getParentLookupApiError,
  getParentLookupPublicImages,
  getParentLookupPublicConfig,
  lookupParentInformation,
  resolveParentLookupImageUrl,
} from '../utils/parentLookupApi';
import {
  ParentLookupClass,
  ParentLookupImage,
  ParentLookupPackage,
  ParentLookupStatus,
  ParentLookupStudent,
  ParentLookupType,
} from '../types/ParentLookup.type';

type TurnstileRenderOptions = {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback': () => void;
  'error-callback': () => void;
};

type TurnstileApi = {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => string | number;
  reset: (widgetId?: string | number) => void;
  remove?: (widgetId: string | number) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TURNSTILE_SCRIPT_ID = 'aquatics-hub-turnstile-script';
const PARENT_LOOKUP_RATE_LIMIT_WINDOW_SECONDS = 60;

const statusColor = (
  status: ParentLookupStatus,
): 'success' | 'warning' | 'error' => {
  if (status === 'Active') return 'success';
  if (status === 'Exhausted') return 'warning';
  return 'error';
};

const compactPhone = (value: string) => value.replace(/[()\s-]/g, '');

const isValidQuery = (lookupType: ParentLookupType, query: string) => {
  const trimmed = query.trim();
  if (lookupType === 'studentCode') {
    const id = Number(trimmed);
    return /^\d+$/.test(trimmed) && Number.isSafeInteger(id) && id > 0;
  }
  return /^\+?\d{8,15}$/.test(compactPhone(trimmed));
};

const formatRetryCountdown = (seconds: number) => {
  const safeSeconds = Math.max(0, Math.ceil(seconds));
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const remainingSeconds = (safeSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
};

const formatDate = (value: string | null, language: string) => {
  if (!value) return '—';
  const date = new Date(`${value}T12:00:00.000+07:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language === 'vi' ? 'vi-VN' : 'en-US', {
    dateStyle: 'medium',
    timeZone: 'Asia/Bangkok',
  }).format(date);
};

const formatSchedule = (
  classItem: ParentLookupClass,
  language: string,
  notAvailable: string,
) => {
  const date = formatDate(classItem.classDate, language);
  const time = [classItem.startTime, classItem.endTime]
    .filter(Boolean)
    .join(' – ');
  return [date, time].filter(Boolean).join(' · ') || notAvailable;
};

type ParentLookupPackageGroup = {
  key: string;
  serviceType: string;
  packages: ParentLookupPackage[];
};

const parentLookupServiceTabOrder = ['TICKET', 'COURSE'];

const groupPackagesByServiceType = (
  packages: ParentLookupPackage[],
): ParentLookupPackageGroup[] => {
  const groups = new Map<string, ParentLookupPackageGroup>();

  packages.forEach((packageItem) => {
    const serviceType = packageItem.serviceType.trim();
    const key = serviceType.toUpperCase() || 'UNKNOWN';
    const existingGroup = groups.get(key);

    if (existingGroup) {
      existingGroup.packages.push(packageItem);
      return;
    }

    groups.set(key, {
      key,
      serviceType: serviceType || key,
      packages: [packageItem],
    });
  });

  return Array.from(groups.values()).sort((left, right) => {
    const leftIndex = parentLookupServiceTabOrder.indexOf(left.key);
    const rightIndex = parentLookupServiceTabOrder.indexOf(right.key);

    if (leftIndex !== -1 || rightIndex !== -1) {
      return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
    }

    return left.serviceType.localeCompare(right.serviceType);
  });
};

const serviceTypeLabel = (
  serviceType: string,
  t: (key: string, vars?: Record<string, unknown>) => string,
) => {
  const key = serviceType.toUpperCase();
  if (key === 'TICKET') return t('parentLookup.serviceTypes.ticket');
  if (key === 'COURSE') return t('parentLookup.serviceTypes.course');
  return serviceType;
};

const CaptchaBlock = ({
  siteKey,
  mockMode,
  token,
  onTokenChange,
  containerRef,
  error,
  onError,
}: {
  siteKey: string;
  mockMode: boolean;
  token: string;
  onTokenChange: (token: string) => void;
  containerRef: RefObject<HTMLDivElement>;
  error: string;
  onError: () => void;
}) => {
  if (mockMode) {
    return (
      <TextField
        fullWidth
        label="Development CAPTCHA token"
        value={token}
        onChange={(event) => onTokenChange(event.target.value)}
        helperText="Local/test mode only. Use the configured mock token."
        inputProps={{ maxLength: 4096 }}
      />
    );
  }

  if (!siteKey) {
    return <Alert severity="warning">{error}</Alert>;
  }

  return (
    <Box>
      <Box ref={containerRef} className="parent-lookup-captcha-widget" />
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }} onClose={onError}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

const PackageCard = ({
  packageItem,
  language,
  t,
}: {
  packageItem: ParentLookupPackage;
  language: string;
  t: (key: string, vars?: Record<string, unknown>) => string;
}) => (
  <Paper variant="outlined" className="parent-lookup-package-card">
    <Stack spacing={1.5}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {packageItem.packageName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {packageItem.serviceType}
          </Typography>
        </Box>
        <Chip
          size="small"
          color={statusColor(packageItem.status)}
          label={t(`parentLookup.status.${packageItem.status.toLowerCase()}`)}
        />
      </Box>

      <Box className="parent-lookup-package-meta">
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('parentLookup.level')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {packageItem.level || t('parentLookup.notAvailable')}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('parentLookup.period')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatDate(packageItem.startDate, language)} –{' '}
            {formatDate(packageItem.endDate, language)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t('parentLookup.remainingSessions')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {packageItem.remainingSessions === null
              ? t('parentLookup.unlimited')
              : packageItem.remainingSessions}
          </Typography>
        </Box>
      </Box>

      <Divider />
      <Typography variant="subtitle2" fontWeight={700}>
        {t('parentLookup.classes')}
      </Typography>
      {packageItem.classes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('parentLookup.noClasses')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {packageItem.classes.map((classItem, index) => (
            <Paper
              variant="outlined"
              className="parent-lookup-class-card"
              key={`${classItem.className}-${classItem.classDate || 'no-date'}-${index}`}
            >
              <Stack spacing={0.35}>
                <Typography variant="body2" fontWeight={700}>
                  {classItem.className}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('parentLookup.teacher')}:{' '}
                  {classItem.teacherNames.join(', ') ||
                    t('parentLookup.notAvailable')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('parentLookup.schedule')}:{' '}
                  {formatSchedule(
                    classItem,
                    language,
                    t('parentLookup.notAvailable'),
                  )}
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}
                >
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${t('parentLookup.level')}: ${classItem.level || t('parentLookup.notAvailable')}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={classItem.status}
                  />
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Stack>
  </Paper>
);

const StudentResultCard = ({
  student,
  language,
  t,
}: {
  student: ParentLookupStudent;
  language: string;
  t: (key: string, vars?: Record<string, unknown>) => string;
}) => {
  const packageGroups = groupPackagesByServiceType(student.packages);
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const selectedGroup =
    packageGroups.find((group) => group.key === selectedServiceType) ||
    packageGroups[0];

  return (
    <Card className="customer-intake-card parent-lookup-student-card opacity-70">
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
           
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={700}>
            {student.displayName}
          </Typography>
          <Chip
            color="primary"
            variant="outlined"
            label={`${t('parentLookup.studentCode')}: ${student.studentCode}`}
          />
        </Box>
        {student.packages.length === 0 ? (
          <Alert severity="info">{t('parentLookup.noPackages')}</Alert>
        ) : (
          <Box>
            <Tabs
              value={selectedGroup?.key || false}
              onChange={(_, value: string) => setSelectedServiceType(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className="parent-lookup-service-tabs"
              aria-label={t('parentLookup.serviceTypesLabel')}
            >
              {packageGroups.map((group) => (
                <Tab
                  key={group.key}
                  value={group.key}
                  label={`${serviceTypeLabel(group.serviceType, t)} (${group.packages.length})`}
                />
              ))}
            </Tabs>

            {selectedGroup && (
              <Box
                role="tabpanel"
                aria-label={serviceTypeLabel(selectedGroup.serviceType, t)}
                sx={{ pt: 2 }}
              >
                <Stack spacing={1.5}>
                  {selectedGroup.packages.map((packageItem, index) => (
                    <PackageCard
                      key={`${packageItem.packageName}-${packageItem.startDate}-${index}`}
                      packageItem={packageItem}
                      language={language}
                      t={t}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Card>
  );
};

const ParentLookup = () => {
  const { t, i18n } = useTranslation();
  const siteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
  const mockMode =
    process.env.NODE_ENV !== 'production' &&
    process.env.REACT_APP_PARENT_LOOKUP_CAPTCHA_PROVIDER === 'mock';
  const captchaContainerRef = useRef<HTMLDivElement>(null);
  const captchaWidgetIdRef = useRef<string | number | null>(null);
  const [lookupType, setLookupType] = useState<ParentLookupType>('studentCode');
  const [query, setQuery] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState<boolean | null>(null);
  const [captchaConfigError, setCaptchaConfigError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [students, setStudents] = useState<ParentLookupStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null,
  );
  const [images, setImages] = useState<ParentLookupImage[]>([]);

  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      try {
        const activeImages = await getParentLookupPublicImages();
        if (!cancelled) setImages(activeImages);
      } catch {
        // The image section is optional and must not prevent parent lookup.
        if (!cancelled) setImages([]);
      }
    };

    void loadImages();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (retryAfterSeconds === null) return undefined;

    if (retryAfterSeconds <= 0) {
      setRetryAfterSeconds(null);
      setError('');
      setSubmitted(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setRetryAfterSeconds((current) =>
        current === null ? null : current - 1,
      );
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [retryAfterSeconds]);

  useEffect(() => {
    let cancelled = false;

    const loadPublicConfig = async () => {
      try {
        const config = await getParentLookupPublicConfig();
        if (cancelled) return;

        setCaptchaRequired(config.captchaRequired);
        setCaptchaConfigError('');
        setCaptchaToken(config.captchaRequired && mockMode ? 'test-token' : '');
      } catch {
        if (cancelled) return;
        setCaptchaRequired(null);
        setCaptchaConfigError(t('parentLookup.captchaConfigError'));
      }
    };

    void loadPublicConfig();

    return () => {
      cancelled = true;
    };
  }, [mockMode, t]);

  useEffect(() => {
    if (
      captchaRequired !== true ||
      mockMode ||
      !siteKey ||
      !captchaContainerRef.current
    ) {
      return undefined;
    }

    let cancelled = false;
    let script: HTMLScriptElement | null = document.getElementById(
      TURNSTILE_SCRIPT_ID,
    ) as HTMLScriptElement | null;

    const renderWidget = () => {
      if (
        cancelled ||
        !captchaContainerRef.current ||
        !window.turnstile ||
        captchaWidgetIdRef.current !== null
      ) {
        return;
      }

      captchaWidgetIdRef.current = window.turnstile.render(
        captchaContainerRef.current,
        {
          sitekey: siteKey,
          callback: (token) => {
            setCaptchaError('');
            setCaptchaToken(token);
          },
          'expired-callback': () => {
            setCaptchaToken('');
            setCaptchaError(t('parentLookup.captchaExpired'));
            setStudents([]);
          },
          'error-callback': () => {
            setCaptchaToken('');
            setCaptchaError(t('parentLookup.captchaError'));
            setStudents([]);
          },
        },
      );
    };

    const handleScriptError = () => {
      if (!cancelled) setCaptchaError(t('parentLookup.captchaError'));
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      if (!script) {
        script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src =
          'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderWidget);
      script.addEventListener('error', handleScriptError);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener('load', renderWidget);
      script?.removeEventListener('error', handleScriptError);
      if (captchaWidgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove?.(captchaWidgetIdRef.current);
      }
      captchaWidgetIdRef.current = null;
    };
  }, [captchaRequired, mockMode, siteKey, t]);

  const resetCaptcha = () => {
    setCaptchaToken(captchaRequired && mockMode ? 'test-token' : '');
    if (captchaWidgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(captchaWidgetIdRef.current);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedQuery = query.trim();
    setError('');
    setRetryAfterSeconds(null);
    setStudents([]);

    if (captchaRequired === null) {
      setError(t('parentLookup.captchaConfigError'));
      return;
    }

    if (!isValidQuery(lookupType, trimmedQuery)) {
      setError(
        lookupType === 'studentCode'
          ? t('parentLookup.invalidStudentCode')
          : t('parentLookup.invalidPhone'),
      );
      return;
    }

    if (captchaRequired && !siteKey && !mockMode) {
      setError(t('parentLookup.captchaNotConfigured'));
      return;
    }

    if (captchaRequired && !captchaToken) {
      setError(t('parentLookup.captchaRequired'));
      return;
    }

    setLoading(true);
    setSubmitted(true);
    try {
      const result = await lookupParentInformation({
        lookupType,
        query: trimmedQuery,
        captchaToken: captchaRequired ? captchaToken : '',
      });
      setStudents(result.students);
    } catch (requestError) {
      const apiError = getParentLookupApiError(
        requestError,
        t('parentLookup.genericError'),
      );
      setStudents([]);
      const isRateLimited = apiError.status === 429;
      setRetryAfterSeconds(
        isRateLimited
          ? Math.max(
              apiError.retryAfterSeconds ??
                PARENT_LOOKUP_RATE_LIMIT_WINDOW_SECONDS,
              1,
            )
          : null,
      );
      setError(apiError.message);
    } finally {
      setLoading(false);
      resetCaptcha();
    }
  };

  const resultsContent =
    students.length > 0 ? (
      <Stack spacing={2} className="parent-lookup-results">
        <Typography variant="h5" component="h2" fontWeight={700}>
          {t('parentLookup.results')}
        </Typography>
        {students.map((student) => (
          <StudentResultCard
            key={student.studentCode}
            student={student}
            language={i18n.language}
            t={t}
          />
        ))}
      </Stack>
    ) : null;

  const noMatchContent =
    submitted && !loading && !error && students.length === 0 ? (
      <Alert severity="info" className="parent-lookup-no-match">
        {t('parentLookup.noMatch')}
      </Alert>
    ) : null;

  return (
    <CustomerIntakePublicShell
      className="parent-lookup-page"
      showHeaderControls
    >
      <Container maxWidth="lg" className="customer-intake-page-container">
        <Box className="customer-intake-hero">
          <Typography className="customer-intake-eyebrow">
            {t('parentLookup.eyebrow')}
          </Typography>
          <Typography
            component="h1"
            variant="h3"
            className="customer-intake-title"
            sx={{ mt: 1 }}
          >
            {t('parentLookup.title')}
          </Typography>
          <Typography variant="body1" className="customer-intake-subtitle">
            {t('parentLookup.subtitle')}
          </Typography>
        </Box>

        <Box
          className={`parent-lookup-search-layout${
            images.length === 0
              ? ' parent-lookup-search-layout--without-poster'
              : ''
          }`}
        >
          {images.length > 0 && (
            <Card className="customer-intake-card parent-lookup-poster-card">
              <Stack spacing={1.5} sx={{ height: '100%' }}>
                <Typography variant="h6" component="h2" fontWeight={700}>
                  {images[0].posterName}
                </Typography>
                <Box className="parent-lookup-poster-frame">
                  <Box
                    component="img"
                    src={resolveParentLookupImageUrl(images[0].url)}
                    alt={images[0].posterName}
                    loading="eager"
                    className="parent-lookup-poster"
                  />
                </Box>
              </Stack>
            </Card>
          )}

          <Box className="parent-lookup-form-column">
            <Card className="customer-intake-card parent-lookup-form-card">
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={2.25}>
                <FormControl>
                  <FormLabel>{t('parentLookup.lookupBy')}</FormLabel>
                  <RadioGroup
                    row
                    value={lookupType}
                    onChange={(event) => {
                      setLookupType(event.target.value as ParentLookupType);
                      setQuery('');
                      setError('');
                      setRetryAfterSeconds(null);
                      setSubmitted(false);
                      setStudents([]);
                    }}
                  >
                    <FormControlLabel
                      value="studentCode"
                      control={<Radio />}
                      label={t('parentLookup.studentCode')}
                    />
                    <FormControlLabel
                      value="phone"
                      control={<Radio />}
                      label={t('parentLookup.phone')}
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  fullWidth
                  label={
                    lookupType === 'studentCode'
                      ? t('parentLookup.studentCode')
                      : t('parentLookup.phone')
                  }
                  placeholder={
                    lookupType === 'studentCode'
                      ? t('parentLookup.studentCodePlaceholder')
                      : t('parentLookup.phonePlaceholder')
                  }
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setError('');
                    setRetryAfterSeconds(null);
                    setSubmitted(false);
                  }}
                  inputProps={{ maxLength: 32 }}
                  autoComplete="off"
                />

                {captchaConfigError && (
                  <Alert severity="error">{captchaConfigError}</Alert>
                )}

                {captchaRequired && (
                  <Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <ShieldOutlined color="primary" fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>
                        {t('parentLookup.captchaLabel')}
                      </Typography>
                    </Stack>
                    <CaptchaBlock
                      siteKey={siteKey}
                      mockMode={mockMode}
                      token={captchaToken}
                      onTokenChange={setCaptchaToken}
                      containerRef={captchaContainerRef}
                      error={
                        captchaError ||
                        (!siteKey ? t('parentLookup.captchaNotConfigured') : '')
                      }
                      onError={() => setCaptchaError('')}
                    />
                  </Box>
                )}

                {error && (
                  <Alert severity="error" icon={<ErrorOutline />}>
                    {error}
                    {retryAfterSeconds !== null && retryAfterSeconds > 0 && (
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        <Typography
                          component="span"
                          variant="body2"
                          fontWeight={700}
                          aria-live="polite"
                        >
                          {t('parentLookup.retryCountdown', {
                            time: formatRetryCountdown(retryAfterSeconds),
                          })}
                        </Typography>
                        <Typography component="span" variant="body2">
                          {t('parentLookup.rateLimitHint')}
                        </Typography>
                      </Stack>
                    )}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Search />
                    )
                  }
                  disabled={
                    loading ||
                    captchaRequired === null ||
                    (captchaRequired === true && !captchaToken) ||
                    (retryAfterSeconds !== null && retryAfterSeconds > 0)
                  }
                >
                  {loading
                    ? t('parentLookup.searching')
                    : t('parentLookup.search')}
                </Button>
                </Stack>
              </Box>
            </Card>
            {images.length > 0 && noMatchContent}
            {images.length > 0 && resultsContent}
          </Box>
        </Box>

        {images.length === 0 && noMatchContent}
        {images.length === 0 && resultsContent}
      </Container>
    </CustomerIntakePublicShell>
  );
};

export default ParentLookup;
